import { bool, string, func, any } from 'prop-types';
import { Button, Spin } from 'antd';
import dynamic from 'next/dynamic';

import { useI18n } from 'context/i18n';

import { IS_CAMPER } from 'utils/constants/system';
import { useProductSheet } from 'context/product-sheet';
import { useState, type Dispatch, type ReactNode, type SetStateAction } from 'react';
import { obtainVoucherPrices } from 'utils/helpers';
import { useIsomorphicLayoutEffect } from 'hooks/useIsomorphicLayoutEffect';
import { useLocale } from 'hooks';
import { useUser } from 'context/user';

const MonthlyInstallMents = dynamic(() => import('./monthlyInstallments').then((mod) => mod.MonthlyInstallMents));
const AppmodeExtra = dynamic(() => import('./appExtra').then((mod) => mod.AppmodeExtraContent));

const marketsWithMonthlyInstallments = IS_CAMPER ? ['TR', 'MX'] : [];

type AddToBagBtnProps = {
  onMouseOver: () => void;
  onMouseLeave: () => void;
  onClick: () => void;
  className?: string;
  label: string;
  loading: boolean;
  market: string;
  setLoading: Dispatch<SetStateAction<boolean>>;
  extraData: any;
  id: string;
  disabled: boolean;
  floatingButton?: boolean;
  selectedSize: string;
  voucherApplied: boolean;
};

export function AddToBagBtn({
  onMouseOver = () => {},
  onMouseLeave = () => {},
  onClick = () => {},
  className,
  label = 'ADD TO BAG',
  loading = false,
  market,
  setLoading,
  id,
  disabled = false,
  floatingButton = false,
  selectedSize,
  voucherApplied,
}: AddToBagBtnProps) {
  const { t, appmode } = useI18n();
  const { locale } = useLocale();
  const { contextFilters, activatedPrevouchers, handleActivatePrevoucher } = useUser();
  const { productSheet } = useProductSheet();
  const [voucherPriceInfo, setVoucherPriceInfo] = useState(null);

  const { prices, brand, sizes, camperlab } = productSheet;

  // TODO: refactorizar para que estos datos se calculen en un hook o en el contexto
  const sizeFound: any = selectedSize ? sizes.find((size) => size?.value === selectedSize) : {};
  const availableSizes = sizes.filter((item) => item.available).map((size) => size.value);

  let firstRowText = brand;
  let secondRowText: ReactNode = prices?.currentFormated ?? '';

  if (!!prices?.pricePerSize) {
    if (!availableSizes.includes(selectedSize)) {
      secondRowText =
        prices?.priceDiscountFromFormatted && prices?.priceDiscountToFormatted ?
          `${prices.priceDiscountFromFormatted} - ${prices.priceDiscountToFormatted}`
        : `${prices?.priceFromFormatted} - ${prices?.priceToFormatted}`;
    } else {
      secondRowText = sizeFound.price?.currentFormated ?? null;
    }
  }

  if (voucherPriceInfo) {
    if (voucherPriceInfo?.message) {
      firstRowText =
        prices.currentFormated && prices.currentFormated !== voucherPriceInfo.info.previousFormated ?
          prices.currentFormated
        : voucherPriceInfo.info.previousFormated;
      secondRowText = <span className="font-primary [&_a]:text-[var(--color-white)]" dangerouslySetInnerHTML={{ __html: voucherPriceInfo.message }} />;
    } else if (voucherPriceInfo && voucherPriceInfo?.info.currentFormated !== voucherPriceInfo?.info.previousFormatted) {
      firstRowText = voucherPriceInfo.info.currentFormated;
      secondRowText = (
        <>
          <div>
            <s>{voucherPriceInfo.info.previousFormated}</s>&nbsp;
            <span className="font-medium">{voucherPriceInfo.info.discountStr ?? `-${voucherPriceInfo.info.discount}%`}</span>
          </div>
          <div className="text-xxs leading-none empty:hidden">{voucherPriceInfo.info.appliedLabel}</div>
        </>
      );
    }
  } else if (prices.previousFormated && prices.currentFormated && prices.previousFormated !== prices.currentFormated) {
    firstRowText = prices.currentFormated;
    secondRowText = (
      <div>
        <s>{prices.previousFormated}</s>&nbsp;<span className="font-medium">-{prices.discount}%</span>
      </div>
    );
  }

  useIsomorphicLayoutEffect(() => {
    const vp = obtainVoucherPrices(productSheet, t, locale, contextFilters, activatedPrevouchers, handleActivatePrevoucher, false, false);
    setVoucherPriceInfo(vp);
  }, [voucherApplied]);

  return (
    // @ts-ignore
    <Spin spinning={loading} id={id}>
      {IS_CAMPER && floatingButton ?
        <div className="flex min-h-18 items-center justify-between gap-x-6 bg-[var(--color-black)] px-8 py-4 font-moderne text-[var(--color-white)]">
          <div className="flex-grow text-sm text-[var(--color-white)]">
            <div className="mb-0 font-moderne font-medium">{firstRowText}</div>
            <div className="mb-0">{secondRowText}</div>
          </div>
          <button
            className="min-h-10 min-w-28 max-w-[48%] flex-shrink-0 bg-[var(--color-white)] px-5 py-0.5 text-[var(--color-black)]"
            disabled={disabled}
            onMouseOver={onMouseOver}
            onMouseLeave={onMouseLeave}
            onClick={() => onClick()}
          >
            {label}
          </button>
        </div>
      : <Button
          block
          size="large"
          type="primary"
          disabled={disabled}
          onMouseOver={onMouseOver}
          onMouseLeave={onMouseLeave}
          onClick={() => onClick()}
          className={className}
        >
          {label}
        </Button>
      }
      {appmode && <AppmodeExtra disabled={disabled} setLoading={setLoading} />}
      {marketsWithMonthlyInstallments.includes(market) ?
        <MonthlyInstallMents />
      : null}
    </Spin>
  );
}
